/**
 * ESO return calculation utilities.
 *
 * Mirrors the WMWM (How Much Would We Make) logic from DealParamsView.tsx,
 * extracted here so it can be reused by the Stranded Value analysis page.
 */

export interface EsoReturnParams {
  exitPricePerShare: number
  totalSharesPledged: number
  pp: number                     // e.g. 0.15 for 15%
  ppSplit: number                // e.g. 1.0 for 100%
  totalUpfrontPayment: number    // exercise cost (what ESO would fund)
  allocationAgreement?: string   // "PPA" | "AA" | "RSU" — default "PPA"
  isPreferentialReturn?: boolean // default true
}

export interface EsoReturnResult {
  grossProceeds: number
  computedLp: number
  esoProceeds: number
  esoProfit: number
  esoRoi: number | null
  optioneeNet: number
}

/**
 * Compute LP from PP, ppSplit, and totalUpfrontPayment.
 *
 * Formula:
 *   lpBeforeTax = (totalUpfront * ppSplit * (1 - PP)) + (totalUpfront * (1 - ppSplit) * 2)
 *
 *   For AA: apply tax adjustment
 *   For Pref Return / RSU: use lpBeforeTax as-is
 *   Standard: max(lpBeforeTax, (1-PP) * totalUpfront)
 */
export function computeLp(
  pp: number,
  ppSplit: number,
  totalUpfrontPayment: number,
  allocationAgreement: string = "PPA",
  isPreferentialReturn: boolean = true,
): number {
  const optioneePp = 1 - pp
  const subjectToPp = totalUpfrontPayment * ppSplit
  const subjectToLp2x = totalUpfrontPayment - subjectToPp
  const lpBeforeTax = subjectToPp * optioneePp + subjectToLp2x * 2

  const AA_TAX_ADJUSTMENT = (1 - 0.2) / (1 - 0.37)

  if (allocationAgreement === "AA") {
    return lpBeforeTax * AA_TAX_ADJUSTMENT - totalUpfrontPayment * (AA_TAX_ADJUSTMENT - 1)
  } else if (isPreferentialReturn || allocationAgreement === "RSU") {
    return lpBeforeTax
  } else {
    return Math.max(lpBeforeTax, optioneePp * totalUpfrontPayment)
  }
}

/**
 * Compute ESO's return at a given exit price per share.
 */
export function computeEsoReturn(params: EsoReturnParams): EsoReturnResult {
  const {
    exitPricePerShare,
    totalSharesPledged,
    pp,
    ppSplit,
    totalUpfrontPayment,
    allocationAgreement = "PPA",
    isPreferentialReturn = true,
  } = params

  const grossProceeds = exitPricePerShare * totalSharesPledged
  const esoParticipation = grossProceeds * pp
  const netAfterEso = Math.max(grossProceeds - esoParticipation, 0)
  const lp = computeLp(pp, ppSplit, totalUpfrontPayment, allocationAgreement, isPreferentialReturn)
  const liquidationPref = allocationAgreement === "RSU" ? 0 : Math.min(lp, netAfterEso)

  let esoProceeds: number
  if (allocationAgreement !== "RSU") {
    esoProceeds = Math.min(Math.max(liquidationPref + esoParticipation, 0), grossProceeds)
  } else {
    esoProceeds = esoParticipation
  }

  const optioneeNet = Math.max(grossProceeds - esoProceeds, 0)
  const esoProfit = esoProceeds - totalUpfrontPayment
  const esoRoi = totalUpfrontPayment > 0 ? esoProceeds / totalUpfrontPayment : null

  return {
    grossProceeds,
    computedLp: lp,
    esoProceeds,
    esoProfit,
    esoRoi,
    optioneeNet,
  }
}

/**
 * Compute ESO's LTV (Loan-to-Value) per share.
 *
 * For PPA: LTV = totalUpfrontPayment / totalSharesPledged
 * For RSU: LTV = totalUpfrontPayment / (pp * totalSharesPledged)
 *
 * This is a per-share dollar amount representing ESO's cost basis per share,
 * not a traditional ratio.
 */
export function computeEsoLtv(
  totalUpfrontPayment: number,
  totalSharesPledged: number,
  pp: number,
  allocationAgreement: string = "PPA",
): number {
  if (totalSharesPledged === 0) return 0
  if (allocationAgreement === "RSU") {
    const denominator = pp * totalSharesPledged
    return denominator === 0 ? 0 : totalUpfrontPayment / denominator
  }
  return totalUpfrontPayment / totalSharesPledged
}

/**
 * Find the breakeven PP — the minimum participation percentage where ESO
 * breaks even (profit >= 0) at a given exit price, assuming ppSplit=1.0 (1x LP).
 *
 * Returns a value between 0 and 1 (e.g. 0.08 = 8% PP).
 * Returns null if breakeven is impossible (exit value < exercise cost).
 */
export function findBreakevenPp(
  exitPricePerShare: number,
  totalSharesPledged: number,
  totalUpfrontPayment: number,
  allocationAgreement: string = "PPA",
): number | null {
  if (totalUpfrontPayment <= 0 || totalSharesPledged <= 0) return null
  if (exitPricePerShare * totalSharesPledged < totalUpfrontPayment) return null

  let lo = 0
  let hi = 1
  for (let i = 0; i < 64; i++) {
    const mid = (lo + hi) / 2
    const result = computeEsoReturn({
      exitPricePerShare,
      totalSharesPledged,
      pp: mid,
      ppSplit: 1.0,
      totalUpfrontPayment,
      allocationAgreement,
    })
    if (result.esoProfit < 0) {
      lo = mid
    } else {
      hi = mid
    }
  }
  return (lo + hi) / 2
}
